import { type FC, useState } from 'react';
import { type IForumQuestion } from '../../../../types/forumQuestion';
import { ForumAnswer } from '../ForumAnwer';
import { useCreateForumAnswer, useRemoveForumQuestion } from '../../../../services/forumQuestion';
import { Spin, Input } from 'antd';
import {
  IconTrash,
  IconMessageCircle,
  IconMessagePlus,
  IconX,
  IconSend,
  IconUser
} from '@tabler/icons-react';
import { useAuth } from '../../../../hooks/useAuth';
import { RoleEnum } from '../../../../types/role';
import clsx from 'clsx';

interface Props {
  question: IForumQuestion
  refetch: () => Promise<any>
}

const ForumQuestion: FC<Props> = ({ question, refetch }) => {
  const { user } = useAuth();

  const isAdmin = (user?.role?.name === RoleEnum.superadmin || user?.role?.name === RoleEnum.admin);

  const { createForumAnswer, loading } = useCreateForumAnswer();
  const { deleteForumQuestion, loading: removing } = useRemoveForumQuestion();

  const [showComments, setShowComments] = useState(false);
  const [addingResponse, setAddingResponse] = useState(false);

  const [response, setResponse] = useState('');

  const toggleComments = () => {
    setShowComments((prev) => !prev);
  };
  const toggleAddingResposne = () => {
    setAddingResponse((prev) => !prev);
  };

  const handleAddAnswer = async () => {
    await createForumAnswer({
      content: response,
      forumQuestion: question._id!
    });
    await refetch();
    setResponse('');
    toggleAddingResposne();
  };

  const handleDeleteQuestion = async () => {
    await deleteForumQuestion(question._id!);
    await refetch();
  };

  const canRemove = isAdmin || question.user._id === user?._id;
  const answersCount = question.answers?.length || 0;

  return (
    <div className={clsx(
      'relative rounded-xl border-2 transition-all duration-300 overflow-hidden',
      'bg-white border-gray-200 shadow-md',
      'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
      'hover:shadow-lg dark:hover:shadow-xl'
    )}>
      {/* Delete button */}
      {canRemove && (
        <button
          disabled={removing}
          onClick={handleDeleteQuestion}
          className={clsx(
            'absolute top-4 right-4 z-10 p-2 rounded-lg transition-all duration-300',
            'bg-red-100 text-red-600 hover:bg-red-200',
            'dark:bg-red-900/40 dark:text-red-400 dark:hover:bg-red-900/60',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
            'dark:focus:ring-offset-surface-dark'
          )}
        >
          <IconTrash size={18} strokeWidth={2} />
        </button>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <h3 className={clsx(
            'text-xl font-bold mb-2 transition-colors duration-300',
            'text-gray-900 dark:text-white'
          )}>
            {question.title}
          </h3>
          <p className={clsx(
            'text-base leading-relaxed transition-colors duration-300',
            'text-gray-700 dark:text-gray-300'
          )}>
            {question.content}
          </p>
        </div>

        {/* User info */}
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-borders">
          <div className="relative">
            {question.user.avatar ? (
              <img
                src={question.user.avatar}
                alt={question.user.name}
                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-borders"
              />
            ) : (
              <div className={clsx(
                'w-10 h-10 rounded-full flex items-center justify-center',
                'bg-gradient-to-br from-blue-500 to-purple-500',
                'dark:from-blue-600 dark:to-purple-600'
              )}>
                <IconUser size={20} className="text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className={clsx(
              'font-semibold text-sm transition-colors duration-300',
              'text-gray-900 dark:text-white',
              'truncate'
            )}>
              {question.user.name}
            </p>
          </div>
          {answersCount > 0 && (
            <div className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold',
              'bg-blue-100 text-blue-700',
              'dark:bg-blue-900/40 dark:text-blue-300'
            )}>
              <IconMessageCircle size={14} />
              <span>{answersCount} {answersCount === 1 ? 'respuesta' : 'respuestas'}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        {!addingResponse && (
          <div className="flex items-center gap-3">
            <button
              onClick={toggleComments}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300',
                'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900',
                'dark:bg-surface-medium dark:text-gray-200 dark:hover:bg-surface-light dark:hover:text-white',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'dark:focus:ring-offset-surface-dark'
              )}
            >
              <IconMessageCircle size={18} strokeWidth={2} />
              <span>{showComments ? 'Ocultar' : 'Ver'} respuestas</span>
            </button>
            <button
              onClick={toggleAddingResposne}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300',
                'bg-blue-600 text-white hover:bg-blue-700',
                'dark:bg-blue-500 dark:hover:bg-blue-600',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'dark:focus:ring-offset-surface-dark',
                'shadow-sm hover:shadow-md'
              )}
            >
              <IconMessagePlus size={18} strokeWidth={2} />
              <span>Añadir respuesta</span>
            </button>
          </div>
        )}

        {/* Add response form */}
        {addingResponse && (
          <div className={clsx(
            'mt-4 p-4 rounded-lg border-2',
            'bg-gray-50 border-gray-200',
            'dark:bg-surface-medium dark:border-borders'
          )}>
            <Input.TextArea
              placeholder="Escribe tu respuesta..."
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={4}
              className={clsx(
                'mb-3',
                '[&_.ant-input]:bg-white',
                '[&_.ant-input]:dark:bg-surface-dark',
                '[&_.ant-input]:border-gray-300',
                '[&_.ant-input]:dark:border-borders',
                '[&_.ant-input]:text-gray-900',
                '[&_.ant-input]:dark:text-white',
                '[&_.ant-input]:placeholder:text-gray-400',
                '[&_.ant-input]:dark:placeholder:text-gray-500',
                '[&_.ant-input]:focus:border-blue-500',
                '[&_.ant-input]:dark:focus:border-blue-400'
              )}
            />
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={toggleAddingResposne}
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300',
                  'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  'dark:bg-surface-light dark:text-gray-300 dark:hover:bg-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                  'dark:focus:ring-offset-surface-dark'
                )}
              >
                <IconX size={16} className="inline mr-1.5" />
                Cancelar
              </button>
              <button
                onClick={handleAddAnswer}
                disabled={!response.trim() || loading}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300',
                  'bg-blue-600 text-white hover:bg-blue-700',
                  'dark:bg-blue-500 dark:hover:bg-blue-600',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  'dark:focus:ring-offset-surface-dark',
                  'shadow-sm hover:shadow-md'
                )}
              >
                {loading ? (
                  <Spin size="small" />
                ) : (
                  <IconSend size={16} strokeWidth={2} />
                )}
                <span>Comentar</span>
              </button>
            </div>
          </div>
        )}

        {/* Answers */}
        {showComments && (
          <div className="mt-6 space-y-3">
            {answersCount > 0 ? (
              question.answers?.map((answer) => (
                <ForumAnswer
                  key={answer._id}
                  user={answer.user.name!}
                  userAvatar={answer.user.avatar}
                  content={answer.content}
                  date={answer.date}
                />
              ))
            ) : (
              <div className={clsx(
                'text-center py-8 rounded-lg',
                'bg-gray-50 dark:bg-surface-medium'
              )}>
                <IconMessageCircle
                  size={32}
                  className={clsx(
                    'mx-auto mb-2',
                    'text-gray-400 dark:text-gray-500'
                  )}
                />
                <p className={clsx(
                  'text-sm transition-colors duration-300',
                  'text-gray-500 dark:text-gray-400'
                )}>
                  Aún no hay respuestas. ¡Sé el primero en responder!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { ForumQuestion };
